/**
 * Thin PostHog wrapper.
 * All calls are no-ops when VITE_POSTHOG_KEY is absent (local dev without a key).
 *
 * Env vars (set in .env):
 *   VITE_POSTHOG_KEY   – your PostHog project API key  (required)
 *   VITE_POSTHOG_HOST  – PostHog instance URL           (optional, defaults to US cloud)
 */
import posthogJs from 'posthog-js';

const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ??
  'https://us.i.posthog.com';

/** Call once at app bootstrap (main.tsx). Safe to call multiple times. */
export function initPostHog(): void {
  if (!KEY) return;
  posthogJs.init(KEY, {
    api_host: HOST,
    capture_pageview: false,  // we fire $pageview manually on route change
    capture_pageleave: true,
    persistence: 'localStorage',
    autocapture: false,       // only intentional events
  });
  posthogJs.register({ app_version: import.meta.env.VITE_APP_VERSION ?? 'unknown' });
}

/**
 * Identify an authenticated user.
 * Call after profile loads; safe to call on every auth state change.
 */
export function identifyUser(
  userId: string,
  properties: { role: string; displayName: string },
): void {
  if (!KEY) return;
  posthogJs.identify(userId, properties);
}

/** Reset identity — call on sign-out. */
export function resetIdentity(): void {
  if (!KEY) return;
  posthogJs.reset();
}

/** Record a custom event with optional properties. */
export function capture(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (!KEY) return;
  posthogJs.capture(event, properties);
}

/** Fire a SPA page-view. Call in a useEffect keyed to location.pathname. */
export function capturePageView(): void {
  if (!KEY) return;
  posthogJs.capture('$pageview');
}
